import { isAppRelativeReturnTo } from './return-to';

// Must match routes.login in lib/auth0.ts.
export const LOGIN_PATH = '/auth/login';

// The home page is where the callback lands without a returnTo, so carrying
// one adds a query param that changes nothing.
const HOME_PATH = '/';

// The SDK's own routes: /auth/login would loop, and /auth/callback and
// /auth/error only make sense mid-handshake, never as a destination.
const AUTH_ROUTE_PREFIX = '/auth/';

// Next.js reports an unresolved dynamic route as `/[slug]` until the router is
// hydrated, so a path still holding its placeholder is not somewhere we can
// send anyone. Rendering the plain login link is the safe fallback.
const UNRESOLVED_DYNAMIC_SEGMENT = '[';

/**
 * True when a path is worth carrying through login as `returnTo`: app-relative
 * (never an open redirect, see isAppRelativeReturnTo), not one of the auth
 * routes themselves, and fully resolved.
 */
export const isReturnableTo = (value: string | undefined): value is string =>
  isAppRelativeReturnTo(value) &&
  value !== HOME_PATH &&
  !value.startsWith(AUTH_ROUTE_PREFIX) &&
  !value.includes(UNRESOLVED_DYNAMIC_SEGMENT);

/**
 * Builds a login link that returns the user to where they started (FFH-2135).
 * nextjs-auth0 v4 stores the `returnTo` query param in the login transaction
 * and hands it back to the onCallback hook, which redirects there on success
 * (see lib/auth0/on-callback.ts); without it every login lands on the home
 * page. Paths we cannot or should not return to simply produce a plain login
 * link rather than a broken one.
 */
export const buildLoginHref = (
  returnTo: string | undefined,
  parameters: Record<string, string> = {}
): string => {
  const searchParameters = new URLSearchParams(parameters);

  if (isReturnableTo(returnTo)) {
    searchParameters.set('returnTo', returnTo);
  }

  const query = searchParameters.toString();
  return query ? `${LOGIN_PATH}?${query}` : LOGIN_PATH;
};
